import type { FlowFilter } from '@/models/flowFilter.type';
import type { RpuServiceUrgences, RpuServiceUrgencesContact } from '@/models/oru_collecte.types';

/**
 * Codes du numéro d'ordre d'un service d'urgence (libellé : clé Tolgee
 * `admin.sus.numero_ordre.<code>`) : 0 adultes polyvalent, 1 pédiatriques, 2 chirurgicales,
 * 3 médicales, 4 psychiatriques, 9 service non autorisé à l'accueil des urgences.
 */
export const susNumeroOrdreCodes = [0, 1, 2, 3, 4, 9] as const;

/** Contact d'un service d'urgence (`id` absent tant que le contact n'est pas créé). */
export type SUSContact = Pick<RpuServiceUrgencesContact, 'libelle' | 'email'> & { id?: number };

export type SUSRow = Pick<
  RpuServiceUrgences,
  | 'id'
  | 'finess_geo'
  | 'numero_ordre'
  | 'nom'
  | 'date_gel'
  | 'transmission_autorisee'
  | 'format_rpu'
> & { contacts?: SUSContact[] };

/** Formats RPU acceptés (libellé : clé Tolgee `admin.sus.format_rpu.<code>`). */
export const susFormatRpuCodes = [
  'E_2013',
  'OSCOUR_V8',
  'SPECIFIQUE_1',
  'SPECIFIQUE_2',
  'SPECIFIQUE_3',
] as const;

/** Modes de calcul automatique des indicateurs (clé Tolgee `admin.sus.calcul_indicateurs.<code>`). */
export const susCalculIndicateursCodes = ['OUI', 'NON', 'PARTIEL'] as const;

/** Valeurs du projet de bascule DPI, stockées telles quelles dans `metadonnees.dpi_bascule`. */
export const susDpiBasculeCodes = ['Oui', 'Non', 'En réflexion'] as const;

export type SusDpiBascule = (typeof susDpiBasculeCodes)[number];

/** Clé Tolgee du libellé de chaque valeur de bascule DPI. */
export const susDpiBasculeLabelKeys: Record<SusDpiBascule, string> = {
  Oui: 'admin.sus.dpi_bascule.oui',
  Non: 'admin.sus.dpi_bascule.non',
  'En réflexion': 'admin.sus.dpi_bascule.en_reflexion',
};

/** Clés de la colonne JSON `metadonnees` saisies dans le formulaire. */
export type SUSMetadonnees = {
  logiciel?: string | null;
  supra_etab?: boolean;
  dpi_version?: string | null;
  dpi_bascule?: SusDpiBascule | null;
  dpi_bascule_precisions?: string | null;
  transmission_rpu_clos?: boolean | null;
};

/** Adresse postale d'un service d'urgence. */
export type SUSAddress = Partial<
  Pick<
    RpuServiceUrgences,
    | 'adr_num_voie'
    | 'adr_type_voie'
    | 'adr_nom_voie'
    | 'adr_lieudit'
    | 'adr_code_postal'
    | 'adr_commune'
  >
>;

/** Position géographique d'un service d'urgence. */
export type SUSCoordinates = Partial<Pick<RpuServiceUrgences, 'latitude' | 'longitude'>>;

/** Paramètres de transmission et seuils d'un service d'urgence. */
export type SUSSettings = Partial<
  Pick<
    RpuServiceUrgences,
    | 'heure_premier_envoi_quotidien'
    | 'reactif_j_1'
    | 'format_rpu'
    | 'seuil_bas_quotidien'
    | 'seuil_haut_quotidien'
    | 'seuil_bas_duree_passage'
    | 'seuil_haut_duree_passage'
    | 'calcul_automatique_indicateurs'
    | 'transmission_autorisee'
  >
>;

/** Champs d'un service d'urgence saisis dans le formulaire (le format RPU et l'heure sont obligatoires). */
export type SUSInput = Pick<
  RpuServiceUrgences,
  'finess_geo' | 'numero_ordre' | 'nom' | 'date_gel'
> &
  SUSAddress &
  SUSCoordinates &
  SUSSettings & { contacts: SUSContact[]; metadonnees?: SUSMetadonnees | null };

/** Service d'urgence complet, tel que chargé pour l'édition et la consultation. */
export type SUSDetail = SUSInput & { id: number };

/** Création (sans `id`) ou mise à jour (avec `id`) d'un service d'urgence. */
export type SUSSaveArg = SUSInput & { id?: number };

/** Colonnes acceptées par le paramètre `orderBy` du flow (liste fermée). */
export const susSortColumns = [
  'id',
  'finess_geo',
  'numero_ordre',
  'nom',
  'date_gel',
  'transmission_autorisee',
  'format_rpu',
] as const satisfies readonly (keyof SUSRow)[];

export type SUSSortColumn = (typeof susSortColumns)[number];

export type SUSSortDirection = 'asc' | 'desc';

/** Champs demandés au flow pour la liste (colonnes de `SusDataTable`). */
export const susListFields = [
  'id',
  'finess_geo',
  'numero_ordre',
  'nom',
  'date_gel',
  'transmission_autorisee',
  'contacts.id',
  'contacts.libelle',
  'contacts.email',
  'format_rpu',
] as const;

/** Champs demandés au flow pour l'édition d'un SU (`SUSDetail`). */
export const susDetailFields = [
  'id',
  'finess_geo',
  'numero_ordre',
  'nom',
  'date_gel',
  'adr_num_voie',
  'adr_type_voie',
  'adr_nom_voie',
  'adr_lieudit',
  'adr_code_postal',
  'adr_commune',
  'latitude',
  'longitude',
  'heure_premier_envoi_quotidien',
  'reactif_j_1',
  'format_rpu',
  'seuil_bas_quotidien',
  'seuil_haut_quotidien',
  'seuil_bas_duree_passage',
  'seuil_haut_duree_passage',
  'calcul_automatique_indicateurs',
  'transmission_autorisee',
  'metadonnees',
  'contacts.id',
  'contacts.libelle',
  'contacts.email',
] as const;

export type SUSQueryArg = {
  page: number;
  pageSize: number;
  /** Champs à renvoyer (tous si absent). */
  fields?: readonly string[];
  orderBy?: `${SUSSortColumn} ${SUSSortDirection}`;
  filter?: FlowFilter;
};

export type SusPage = {
  rows: SUSRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
};
